import React, { useState } from "react";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { ChevronDown, ChevronRight, FileText } from "lucide-react";
import { FormField, Input, Select } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData } from "../../types";
import {
  BL_TYPE_OPTIONS,
  NUMBER_OF_ORIGINALS_OPTIONS,
  FREIGHT_PAYMENT_TERMS_OPTIONS,
} from "../../constants/shipmentFormConstants";
import { useTranslation } from "react-i18next";

interface BillOfLadingSectionProps {
  register: UseFormRegister<CreateShipmentData>;
  errors: FieldErrors<CreateShipmentData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
}

// House Bill of Lading document fields (SEA only) — a collapsible accordion
// shown after the Sea Freight Details block. These are the values that print
// onto the HBL document/preview.
export const BillOfLadingSection: React.FC<BillOfLadingSectionProps> = ({
  register,
  errors,
  readonly,
  getErrorMessage,
}) => {
  const { t } = useTranslation("shipments");
  const [isCollapsed, setIsCollapsed] = useState(true);
  const seaErrors = errors.seaDetails;
  const notifyErrors = seaErrors?.notifyParty;

  return (
    <SectionCard
      icon={FileText}
      title={t("bol.title")}
      collapsible
      onToggle={() => setIsCollapsed((p) => !p)}
      actions={
        isCollapsed ? (
          <ChevronRight className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )
      }
    >
      {isCollapsed ? null : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
            <div className="space-y-1">
              <FormField
                layout="inline"
                size="sm"
                required
                label={t("sea.blNumber")}
                error={getErrorMessage(seaErrors?.blNumber)}
              >
                <Input
                  {...register("seaDetails.blNumber")}
                  error={!!seaErrors?.blNumber}
                  placeholder={t("sea.blNumberPlaceholder")}
                  readOnly={readonly}
                  size="sm"
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                required
                label={t("sea.blDate")}
                error={getErrorMessage(seaErrors?.blDate)}
              >
                <Input
                  {...register("seaDetails.blDate")}
                  type="date"
                  error={!!seaErrors?.blDate}
                  readOnly={readonly}
                  size="sm"
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                required
                label={t("sea.numberOfOriginals")}
                error={getErrorMessage(seaErrors?.numberOfOriginals)}
              >
                <Select
                  {...register("seaDetails.numberOfOriginals")}
                  options={NUMBER_OF_ORIGINALS_OPTIONS}
                  error={!!seaErrors?.numberOfOriginals}
                  disabled={readonly}
                  placeholder={t("sea.numberOfOriginalsPlaceholder")}
                  size="sm"
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                required
                label={t("sea.blType")}
                error={getErrorMessage(seaErrors?.blType)}
              >
                <Select
                  {...register("seaDetails.blType")}
                  options={BL_TYPE_OPTIONS}
                  error={!!seaErrors?.blType}
                  disabled={readonly}
                  placeholder={t("sea.blTypePlaceholder")}
                  size="sm"
                />
              </FormField>
            </div>

            <div className="space-y-1">
              <FormField
                layout="inline"
                size="sm"
                required
                label={t("sea.freightPaymentTerms")}
                error={getErrorMessage(seaErrors?.freightPaymentTerms)}
              >
                <Select
                  {...register("seaDetails.freightPaymentTerms")}
                  options={FREIGHT_PAYMENT_TERMS_OPTIONS}
                  error={!!seaErrors?.freightPaymentTerms}
                  disabled={readonly}
                  placeholder={t("sea.freightPaymentTermsPlaceholder")}
                  size="sm"
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("sea.placeOfReceipt")}
                error={getErrorMessage(seaErrors?.placeOfReceipt)}
              >
                <Input
                  {...register("seaDetails.placeOfReceipt")}
                  error={!!seaErrors?.placeOfReceipt}
                  placeholder={t("sea.placeOfReceiptPlaceholder")}
                  readOnly={readonly}
                  size="sm"
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("sea.placeOfDelivery")}
                error={getErrorMessage(seaErrors?.placeOfDelivery)}
              >
                <Input
                  {...register("seaDetails.placeOfDelivery")}
                  error={!!seaErrors?.placeOfDelivery}
                  placeholder={t("sea.placeOfDeliveryPlaceholder")}
                  readOnly={readonly}
                  size="sm"
                />
              </FormField>
            </div>
          </div>

          {/* Notify Party — optional (HBL) */}
          <div className="pt-3 border-t border-gray-100">
            <h4 className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {t("notifyParty.title")}
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
              <div className="space-y-1">
                <FormField
                  layout="inline"
                  size="sm"
                  label={t("notifyParty.company")}
                  error={getErrorMessage(notifyErrors?.company)}
                >
                  <Input
                    {...register("seaDetails.notifyParty.company")}
                    error={!!notifyErrors?.company}
                    placeholder={t("notifyParty.companyPlaceholder")}
                    readOnly={readonly}
                    size="sm"
                  />
                </FormField>

                <FormField
                  layout="inline"
                  size="sm"
                  label={t("notifyParty.contact")}
                  error={getErrorMessage(notifyErrors?.contact)}
                >
                  <Input
                    {...register("seaDetails.notifyParty.contact")}
                    error={!!notifyErrors?.contact}
                    placeholder={t("notifyParty.contactPlaceholder")}
                    readOnly={readonly}
                    size="sm"
                  />
                </FormField>
              </div>

              <div className="space-y-1">
                <FormField
                  layout="inline"
                  size="sm"
                  label={t("labels.email")}
                  error={getErrorMessage(notifyErrors?.email)}
                >
                  <Input
                    {...register("seaDetails.notifyParty.email")}
                    type="email"
                    error={!!notifyErrors?.email}
                    placeholder={t("placeholders.email")}
                    readOnly={readonly}
                    size="sm"
                  />
                </FormField>

                <FormField
                  layout="inline"
                  size="sm"
                  label={t("labels.phone")}
                  error={getErrorMessage(notifyErrors?.phone)}
                >
                  <Input
                    {...register("seaDetails.notifyParty.phone")}
                    error={!!notifyErrors?.phone}
                    placeholder={t("placeholders.phone")}
                    readOnly={readonly}
                    size="sm"
                  />
                </FormField>
              </div>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

BillOfLadingSection.displayName = "BillOfLadingSection";
